import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { Typography } from '@components/Typography';
import Autocomplete from '@components/Autocomplete';
import MediaUploaderMulti from '@components/MediaUploaderMulti';
import { Select } from '@components/Select';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { ProfileName } from '@constants/integrations';
import { alertActions, profilesMediaActions } from '@actions';
import { profilesMediaService } from '@services';
import { handleApiError } from '@helpers/api';
import { KB, MB } from '@constants/file';
import { Content, Modal } from './styles';

const UploadModal = ({ isOpen, handleClose }) => {
    const { t } = useTranslation();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const locations = useSelector(state => state.account?.account?.companies || []);
    const { data } = useSelector(state => state.profilesMedia);

    const dispatch = useDispatch();
    const { mapLocationsToFormValue, getLocationsOptions } = useLocations();

    const categoriesOptions = [
        { value: 1, label: t('ProfilesMedia.exterior') },
        { value: 2, label: t('ProfilesMedia.interior') },
        { value: 3, label: t('ProfilesMedia.product') },
        { value: 4, label: t('ProfilesMedia.atWork') },
        { value: 5, label: t('ProfilesMedia.foodAndDrink') },
        { value: 6, label: t('ProfilesMedia.menu') },
        { value: 7, label: t('ProfilesMedia.commonArea') },
        { value: 8, label: t('ProfilesMedia.rooms') },
        { value: 9, label: t('ProfilesMedia.teams') },
    ];

    const UploadSchema = yup.object().shape({
        locations: yup.array().min(1, 'validation.required'),
        category: yup.number(),
        photoUrls: yup.array().min(1, 'validation.required'),
    });

    const resolver = useYupValidationResolver(UploadSchema);

    const {
        getValues,
        setValue,
        control,
        reset,
        formState: { isValid, isDirty, errors },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            locations: mapLocationsToFormValue(
                locations.filter(l => l.profiles?.some(p => p.type === ProfileName.GoogleOfficial)),
            ),
            category: '',
            photoUrls: [],
        },
    });

    const locationsOptions = useMemo(() => {
        const filterCb = l => l?.profiles?.some(p => p.type === ProfileName.GoogleOfficial);
        return getLocationsOptions(filterCb);
    }, []);

    const onCloseModal = () => {
        handleClose(false);
        reset();
    };

    const onUpload = async () => {
        const values = getValues();
        setIsSubmitting(true);
        onCloseModal();
        try {
            const newMedia = await profilesMediaService.uploadMedia({
                ...values,
                locations: values.locations.map(item => item.id),
            });
            dispatch(profilesMediaActions.set([...newMedia, ...data]));
            dispatch(alertActions.success(t('alertMessages.uploadSuccess')));
        } catch (error) {
            if (error.errorCode === 'google_validation_failed' && error.reasons?.length > 0) {
                error.reasons.forEach(reason => {
                    dispatch(alertActions.error(reason));
                });
            } else {
                handleApiError(error, 'alertMessages.uploadFail');
            }
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            handleClose={onCloseModal}
            title={t('ProfilesMedia.uploadPhotos')}
            onPrimaryAction={onUpload}
            primaryActionText={t('ProfilesMedia.uploadPhotos')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty || !!errors?.message}
            onSecondaryAction={onCloseModal}
            secondaryActionText={t('buttons.cancel')}
        >
            <Content>
                <Controller
                    control={control}
                    name="locations"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Autocomplete
                            {...field}
                            ref={ref}
                            label={t('common.locations')}
                            options={locationsOptions}
                            value={field.value}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                            onChangeAutoComplete={items => {
                                setValue('locations', items.length ? items : [], {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                            }}
                            required
                            error={error}
                            className="locations-autocomplete"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="category"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            onChange={({ target: { value } }) => {
                                setValue('category', value, { shouldDirty: true });
                            }}
                            ref={ref}
                            label={t('common.category', { count: 0 })}
                            fullWidth
                            options={categoriesOptions}
                            style={{ gridArea: 'select2' }}
                        />
                    )}
                />
            </Content>
            <Typography
                variant="caption"
                sx={{ color: 'rgba(0, 0, 0, 0.6)', marginBottom: '12px', display: 'inline-block' }}
            >
                {t('ProfilesMedia.photosFormat')}
            </Typography>
            <MediaUploaderMulti
                limit={10}
                imagesValidation={{ maxSize: 5 * MB, minSize: 10 * KB }}
                extensions=".png, .jpg, .jpeg"
                onChange={updatedMedia =>
                    setValue(
                        'photoUrls',
                        updatedMedia.map(m => m.link),
                        { shouldDirty: true, shouldValidate: true },
                    )
                }
                imageServiceCallback={data => profilesMediaService.uploadProfilesImages(data)}
            />
        </Modal>
    );
};

export default UploadModal;
